import * as React from 'react';
import { IBac, IPlanningEntry, ContentType } from '../../models';
import { PlanningCell } from './PlanningCell';
import { PlanningLegend } from './PlanningLegend';
import { formatMonthLabel, isCurrentMonth } from '../shared/MonthUtils';
import { useData } from '../../core/context/ProvidersContext';
import styles from './PlanningGrid.module.scss';

export interface IPlanningGridProps {
  bacs: IBac[];
  entries: IPlanningEntry[];
  months: string[];
  depotNames: string[];
  onDataChanged: () => void;
}

export const PlanningGrid: React.FC<IPlanningGridProps> = ({
  bacs, entries, months, depotNames, onDataChanged,
}) => {
  const data = useData();
  const [filterDepot, setFilterDepot] = React.useState('');
  const [filterBac, setFilterBac] = React.useState('');
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const thDepotRef = React.useRef<HTMLTableCellElement>(null);
  const thBacRef = React.useRef<HTMLTableCellElement>(null);
  const thVolRef = React.useRef<HTMLTableCellElement>(null);

  const updateStickyOffsets = React.useCallback((): void => {
    const el = wrapperRef.current;
    const d = thDepotRef.current;
    const b = thBacRef.current;
    const v = thVolRef.current;
    if (!el || !d || !b || !v) return;

    const dw = d.offsetWidth;
    const bw = b.offsetWidth;
    const vw = v.offsetWidth;
    el.style.setProperty('--col-depot-w', `${dw}px`);
    el.style.setProperty('--col-bac-left', `${dw}px`);
    el.style.setProperty('--col-vol-left', `${dw + bw}px`);
    el.style.setProperty('--col-restr-left', `${dw + bw + vw}px`);
  }, []);

  React.useLayoutEffect(() => {
    updateStickyOffsets();
  });

  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => updateStickyOffsets());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateStickyOffsets]);

  const entryMap = React.useMemo(() => {
    const map: Record<string, IPlanningEntry> = {};
    entries.forEach(e => {
      const key = `${e.BacId}-${e.Mois.substring(0, 7)}`;
      map[key] = e;
    });
    return map;
  }, [entries]);

  const filteredBacs = React.useMemo(() => {
    let result = bacs;
    if (filterDepot) {
      result = result.filter(b => b.DepotTitle === filterDepot);
    }
    if (filterBac) {
      const search = filterBac.toLowerCase();
      result = result.filter(b => b.Title.toLowerCase().includes(search));
    }
    return result;
  }, [bacs, filterDepot, filterBac]);

  const depotGroups = React.useMemo(() => {
    const groups: Record<string, IBac[]> = {};
    filteredBacs.forEach(b => {
      const depot = b.DepotTitle || 'Sans dépôt';
      if (!groups[depot]) groups[depot] = [];
      groups[depot].push(b);
    });
    return groups;
  }, [filteredBacs]);

  const handleCellUpdate = async (
    bacId: number, mois: string, contenu: string, typeContenu: ContentType
  ): Promise<void> => {
    await data.upsertPlanningEntry(bacId, mois, contenu, typeContenu);
    onDataChanged();
  };

  const totalCols = 4 + months.length + 3;

  const selectedLabel = filterDepot || 'Tous les dépôts';

  return (
    <div>
      <PlanningLegend />
      <div className={styles.wrapper} ref={wrapperRef}>
        <table className={styles.grid}>
          <thead>
            <tr>
              <th className={styles.fixedColDepot} ref={thDepotRef}>
                <div className={styles.headerWithFilter}>
                  <span className={styles.headerLabel}>{selectedLabel}</span>
                  <select
                    className={styles.headerSelect}
                    value={filterDepot}
                    onChange={e => setFilterDepot(e.target.value)}
                    title="Filtrer par dépôt"
                  >
                    <option value="">Tous les dépôts</option>
                    {depotNames.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </th>
              <th className={styles.fixedColBac} ref={thBacRef}>
                <div className={styles.headerWithFilter}>
                  <span>Bac</span>
                  <input
                    className={styles.headerInput}
                    type="text"
                    placeholder="⌕"
                    value={filterBac}
                    onChange={e => setFilterBac(e.target.value)}
                    title="Rechercher un bac"
                  />
                </div>
              </th>
              <th className={styles.fixedColVol} ref={thVolRef}>Vol. (m³)</th>
              <th className={styles.fixedColRestr}>Restr.</th>
              {months.map(m => (
                <th
                  key={m}
                  className={isCurrentMonth(m) ? styles.currentMonthHeader : undefined}
                >
                  {formatMonthLabel(m)}
                </th>
              ))}
              <th>Pt dépôt</th>
              <th>Info clients</th>
              <th>Commentaire</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(depotGroups).map(([depotName, depotBacs]) => (
              <React.Fragment key={depotName}>
                <tr className={styles.depotRow}>
                  <td colSpan={totalCols}>{depotName}</td>
                </tr>
                {depotBacs.map((bac, rowIdx) => {
                  const restrictionText = bac.RestrictionClients
                    ? (typeof bac.RestrictionClients === 'number'
                      ? `${Math.round(bac.RestrictionClients * 100)}%`
                      : String(bac.RestrictionClients))
                    : '';
                  const isDeloc = restrictionText.toLowerCase().includes('déloc') ||
                    restrictionText.toLowerCase().includes('deloc');

                  return (
                    <tr key={bac.Id} className={styles.dataRow} style={{ animationDelay: `${rowIdx * 0.03}s` }}>
                      <td className={styles.cellDepot}>{bac.DepotTitle}</td>
                      <td className={styles.cellBac}>{bac.Title}</td>
                      <td className={styles.cellVolume}>
                        {bac.VolumeUtile ? bac.VolumeUtile.toLocaleString('fr-FR') : ''}
                      </td>
                      <td className={`${styles.cellRestriction} ${
                        isDeloc ? styles.restrictionDeloc :
                        (bac.RestrictionClients && bac.RestrictionClients < 0) ? styles.restrictionNegative : ''
                      }`}>
                        {restrictionText}
                      </td>
                      {months.map(m => {
                        const key = `${bac.Id}-${m.substring(0, 7)}`;
                        const entry = entryMap[key];
                        return (
                          <PlanningCell
                            key={key}
                            contenu={entry ? entry.Contenu : ''}
                            mois={m}
                            onUpdate={(contenu, type) =>
                              handleCellUpdate(bac.Id, m, contenu, type)
                            }
                          />
                        );
                      })}
                      <td className={styles.cellComm}>
                        <span className={bac.PointDepot ? styles.commOui : styles.commNon}>
                          {bac.PointDepot ? 'oui' : 'non'}
                        </span>
                      </td>
                      <td className={styles.cellComm}>
                        <span className={bac.InfoClients ? styles.commOui : styles.commNon}>
                          {bac.InfoClients ? 'oui' : 'non'}
                        </span>
                      </td>
                      <td className={styles.cellComment} title={bac.Commentaire}>
                        {bac.Commentaire}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
